import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Course,
  CreateTrainingAttestationInput,
  Invitation,
  OrgMember,
  TrainingAttestation,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Badge, Button, Dialog, Field, Input, Select, useToast } from '../../components/ui.js';

const ALL_COURSES = '__all__';
const DEFAULT_SOURCE = 'Previous training platform';

interface CourseOutcome {
  courseId: string;
  title: string;
  ok: boolean;
  message: string;
}

/** Today as YYYY-MM-DD (UTC, matching the API's date check). */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Record prior completions (attestations) for a member — or an invited email
 * that has not joined yet — across one or every active course. One request is
 * posted per course so partial success is reported course by course.
 */
export function AttestationDialog({
  courses,
  open,
  onClose,
}: {
  courses: Course[];
  open: boolean;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const members = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: open,
  });
  const invitations = useQuery({
    queryKey: ['invitations', org.orgId],
    queryFn: () => apiFetch<Invitation[]>(`/orgs/${org.orgId}/invitations`),
    enabled: open,
  });

  // "user:<id>" for members, "email:<address>" for pending invitees.
  const [person, setPerson] = useState('');
  const [courseId, setCourseId] = useState(ALL_COURSES);
  const [completedAt, setCompletedAt] = useState(today());
  const [source, setSource] = useState(DEFAULT_SOURCE);
  const [replace, setReplace] = useState(false);
  const [outcomes, setOutcomes] = useState<CourseOutcome[]>([]);

  const record = useMutation({
    mutationFn: async () => {
      const targets = courseId === ALL_COURSES ? courses : courses.filter((c) => c.id === courseId);
      const who: Pick<CreateTrainingAttestationInput, 'userId' | 'email'> = person.startsWith('user:')
        ? { userId: person.slice('user:'.length) }
        : { email: person.slice('email:'.length) };
      const results: CourseOutcome[] = [];
      for (const course of targets) {
        try {
          await apiFetch<TrainingAttestation>(`/orgs/${org.orgId}/training/attestations`, {
            method: 'POST',
            body: {
              ...who,
              courseId: course.id,
              completedAt,
              source: source.trim(),
              replace,
            } satisfies CreateTrainingAttestationInput,
          });
          results.push({ courseId: course.id, title: course.title, ok: true, message: 'Recorded' });
        } catch (err) {
          results.push({
            courseId: course.id,
            title: course.title,
            ok: false,
            message: err instanceof ApiRequestError ? err.message : 'Request failed',
          });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      setOutcomes(results);
      const recorded = results.filter((r) => r.ok).length;
      if (recorded > 0) {
        toast(
          `Recorded ${recorded} prior completion${recorded === 1 ? '' : 's'}${
            recorded < results.length ? ` (${results.length - recorded} skipped)` : ''
          }.`,
          'success',
        );
        void queryClient.invalidateQueries({ queryKey: ['training-org-progress', org.orgId] });
        void queryClient.invalidateQueries({ queryKey: ['training-attestations', org.orgId] });
        void queryClient.invalidateQueries({ queryKey: ['training-progress', org.orgId] });
        // The admin may have recorded their own completion.
        void queryClient.invalidateQueries({ queryKey: ['training-courses', org.orgId] });
        void queryClient.invalidateQueries({ queryKey: ['courses', org.orgId] });
      } else {
        toast('Nothing was recorded — see the results below.', 'error');
      }
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error'),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!person) return;
    setOutcomes([]);
    record.mutate();
  };

  const close = () => {
    setOutcomes([]);
    onClose();
  };

  const pending = (invitations.data ?? []).filter((inv) => !inv.acceptedAt);

  return (
    <Dialog open={open} onClose={close} title="Record prior completion">
      <div className="attestation-dialog">
        <p className="attestation-intro">
          Record that someone completed a course before joining Zest Assure. Attested completions
          count towards training evidence for 12 months from the completion date and are marked
          as attested wherever progress is shown.
        </p>
        <form onSubmit={submit}>
          <Field label="Member" required hint="People with a pending invitation are linked when they join">
            {(props) => (
              <Select {...props} required value={person} onChange={(e) => setPerson(e.target.value)}>
                <option value="">Select a member…</option>
                <optgroup label="Members">
                  {(members.data ?? []).map((m) => (
                    <option key={m.user.id} value={`user:${m.user.id}`}>
                      {m.user.name} ({m.user.email})
                    </option>
                  ))}
                </optgroup>
                {pending.length > 0 && (
                  <optgroup label="Pending invitations">
                    {pending.map((inv) => (
                      <option key={inv.id} value={`email:${inv.email}`}>
                        {inv.email} (invited)
                      </option>
                    ))}
                  </optgroup>
                )}
              </Select>
            )}
          </Field>
          <Field label="Course" required>
            {(props) => (
              <Select {...props} value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value={ALL_COURSES}>All active courses ({courses.length})</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Completion date" required hint="Validity runs 12 months from this date">
            {(props) => (
              <Input
                {...props}
                type="date"
                required
                max={today()}
                value={completedAt}
                onChange={(e) => setCompletedAt(e.target.value)}
              />
            )}
          </Field>
          <Field label="Source" required hint="Where the completion was recorded, e.g. the previous platform">
            {(props) => (
              <Input
                {...props}
                required
                maxLength={500}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              />
            )}
          </Field>
          <div className="field">
            <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              Replace existing attestations for the selected course(s)
            </label>
          </div>

          {outcomes.length > 0 && (
            <ul className="attestation-outcomes" aria-label="Results per course">
              {outcomes.map((o) => (
                <li key={o.courseId}>
                  <Badge tone={o.ok ? 'success' : 'warning'}>{o.ok ? 'Recorded' : 'Skipped'}</Badge>
                  <span className="attestation-outcome-title">{o.title}</span>
                  {!o.ok && <span className="attestation-outcome-message">{o.message}</span>}
                </li>
              ))}
            </ul>
          )}

          <div className="dialog-actions">
            <Button variant="secondary" onClick={close}>
              Close
            </Button>
            <Button
              type="submit"
              disabled={!person || !completedAt || source.trim() === ''}
              loading={record.isPending}
            >
              Record completion{courseId === ALL_COURSES ? 's' : ''}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
